/**
 * OnboardingAutoFill.jsx — v213.7 (C-A-OB-FIX)
 *
 * Screen 2 of 7. Auto-fill profile from a prior medical record.
 *
 * v213.7 changes:
 *  - Renamed eyebrow from "the smart shortcut" → "Auto-Fill Profile"
 *  - Whole dropzone is the click target (no separate Upload button below).
 *  - Drag-and-drop supported on top of file picker.
 *  - "I don't have one to upload" upgraded from text link to real
 *    SecondaryButton (48px outlined navy ring).
 *
 * TEST 3.03 (patient push 1): the step no longer jumps ahead after a parse. The
 * patient presses Continue; every record read so far is listed above the dropzone
 * (from the store, so it is still there after Back), and "Upload another record"
 * adds the next one to the lists instead of replacing them.
 */
import { useRef, useState } from 'react'
import { FileText, Sparkles, Lock, CheckCircle, Upload } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useOnboardingStore } from '../../stores'
import { profileAPI } from '../../services/api'
import {
  OnboardingShell,
  ScreenHeader,
  ScreenIntro,
  PrimaryButton,
  SecondaryButton,
  Footer,
  ScrollBody,
  useScrollToTop,
  NAVY,
  BLUE,
  LIGHT_BLUE,
  ICE_BLUE,
  GREEN,
  TEXT_MUTED,
  TEXT_HINT,
  PROFILE_STEPS,
} from './onboardingPrimitives'
import { fitsGatewayAsDataUrlJson } from '../../utils/uploadLimit'

export default function OnboardingAutoFill({ onNext, onBack }) {
  const { t } = useI18n()
  const fileInputRef = useRef(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [dragActive, setDragActive] = useState(false)

  const applyAutofillResult = useOnboardingStore((s) => s.applyAutofillResult)
  const markAutofillSkipped = useOnboardingStore((s) => s.markAutofillSkipped)
  const autofillDocuments = useOnboardingStore((s) => s.autofillDocuments) || []

  useScrollToTop('autofill')

  const handleFile = async (file) => {
    if (!file) return
    if (file.size > 4 * 1024 * 1024) {
      setError(t('errors2.fileTooLarge', 'File is too large.'))
      return
    }
    // ★ PROD-228: the request carries the file as base64 (4/3 of it), so a file under the
    //   size above can still be over Lambda's 6 MB request quota (uploadLimit.js); AWS
    //   refuses that before orella-api runs, with no CORS, and it reads as a network error.
    if (!fitsGatewayAsDataUrlJson(file)) {
      setError(t('detect.uploadFailedTooLarge', { name: file.name, defaultValue: '"{{name}}" is too large to upload.' }))
      return
    }

    setError(null)
    setProcessing(true)

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })

      const response = await profileAPI.parseMedicalDocument({
        file: base64,
        fileType: file.type,
        fileName: file.name,
      })

      if (response?.success && response.data) {
        // TEST 3.03: no auto-advance. The step stays, lists this record, and waits.
        applyAutofillResult(response.data, file.name)
        setProcessing(false)
      } else {
        setError(t('onboarding.autofill.noData', "Couldn't read this document. You can keep going and add details manually."))
        setProcessing(false)
      }
    } catch (err) {
      console.error('[OnboardingAutoFill] extraction failed:', err)
      setError(t('onboarding.autofill.failed', 'Something went wrong. You can try again or skip.'))
      setProcessing(false)
    }
  }

  const handleSkip = () => {
    markAutofillSkipped()
    onNext()
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!dragActive) setDragActive(true)
  }
  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
  }
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) handleFile(file)
  }

  // ---------- Processing overlay ----------
  if (processing) {
    return (
      <OnboardingShell>
        <div className="flex flex-col px-6" style={{ paddingTop: 56, paddingBottom: 26, minHeight: '70vh' }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 40 }}>
            <Sparkles size={20} style={{ color: BLUE }} />
            <span
              style={{
                fontSize: 14,
                color: BLUE,
                fontWeight: 500,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {t('onboarding.autofill.processingLabel', 'Auto-Fill Profile')}
            </span>
          </div>

          <div
            className="rounded-full flex items-center justify-center"
            style={{
              width: 80, height: 80,
              background: ICE_BLUE,
              border: `2px solid ${BLUE}`,
              animation: 'sparkle-rotate 3s ease-in-out infinite',
              marginBottom: 22,
            }}
          >
            <Sparkles size={32} color={BLUE} />
          </div>

          <h2
            className="font-medium"
            style={{ fontSize: 22, lineHeight: 1.2, color: NAVY, marginBottom: 8 }}
          >
            {t('onboarding.autofill.processingTitle', 'Reading your record')}
          </h2>
          <p style={{ fontSize: 15, color: TEXT_MUTED, maxWidth: 280, lineHeight: 1.5 }}>
            {t('onboarding.autofill.processingSubtitle', 'Extracting medications, allergies, and history.')}
          </p>

          <div style={{ marginTop: 32 }}>
            <div style={{ width: '100%', height: 5, background: '#E5EDF3', borderRadius: 999, overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  background: BLUE,
                  width: '100%',
                  animation: 'autofill-bar 3.6s ease-out forwards',
                }}
              />
            </div>
          </div>
        </div>

        <style>{`
          @keyframes sparkle-rotate {
            0%,100% { transform: rotate(0deg) scale(1); opacity: 1; }
            50% { transform: rotate(180deg) scale(1.05); opacity: .75; }
          }
          @keyframes autofill-bar {
            0% { width: 0%; }
            70% { width: 85%; }
            100% { width: 100%; }
          }
        `}</style>
      </OnboardingShell>
    )
  }

  // ---------- Default state ----------
  return (
    <OnboardingShell>
      <ScreenHeader onBack={onBack} step={1} total={PROFILE_STEPS} />
      <ScreenIntro
        eyebrow={t('onboarding.autofill.eyebrow', 'Your records')}
        title={t('onboarding.autofill.title', 'Have records to upload?')}
        dialogue={t('onboarding.autofill.dialogue', "Share a visit note, discharge summary or med list and I'll fill in your allergies, medications and history — you just confirm each.")}
      />

      <ScrollBody>
        {autofillDocuments.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <p className="font-medium" style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 6 }}>
              {t('onboarding.autofill.addedHeading')}
            </p>
            <div className="flex flex-col gap-1.5">
              {autofillDocuments.map((doc) => (
                <div key={doc.id} className="flex items-center gap-2" style={{ fontSize: 14, color: NAVY }}>
                  <CheckCircle size={16} color={GREEN} className="flex-shrink-0" />
                  <span className="truncate">{doc.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Whole dropzone is the click target. Drag-and-drop also works.
            No separate Upload button below — this IS the upload action. */}
        <button
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className="w-full text-left transition"
          style={{
            padding: 20,
            borderRadius: 18,
            background: dragActive ? '#fff' : ICE_BLUE,
            border: `2px dashed ${dragActive ? BLUE : LIGHT_BLUE}`,
            cursor: 'pointer',
            marginBottom: 16,
          }}
        >
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <div
                className="rounded-[14px] flex items-center justify-center"
                style={{ width: 60, height: 60, background: '#fff' }}
              >
                <FileText size={28} color={BLUE} />
              </div>
              <div
                className="absolute"
                style={{
                  top: -6, right: -6,
                  animation: 'sparkle-rotate 3s ease-in-out infinite',
                }}
              >
                <Sparkles size={20} color={BLUE} />
              </div>
            </div>
            <div className="flex-1">
              <p className="font-medium" style={{ fontSize: 15, color: NAVY, marginBottom: 4 }}>
                {t('onboarding.autofill.acceptLabel', 'Referrals, visit notes, discharge summaries')}
              </p>
              <p style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.4 }}>
                {t('onboarding.autofill.acceptHint', 'Tap to choose a file or drag one in. PDF or photo.')}
              </p>
            </div>
          </div>
          <div
            className="flex items-center justify-center gap-2"
            style={{
              marginTop: 14,
              padding: '9px 14px',
              borderRadius: 999,
              background: BLUE,
              color: '#fff',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <Upload size={16} />
            <span>{t('onboarding.autofill.cta', 'Upload medical record')}</span>
          </div>
        </button>

        <div className="flex flex-col gap-2" style={{ paddingLeft: 4 }}>
          <div className="flex items-center gap-2" style={{ fontSize: 13, color: TEXT_MUTED }}>
            <Lock size={14} color={GREEN} />
            <span>{t('onboarding.autofill.privacy1', 'Documents are deleted after extraction')}</span>
          </div>
          <div className="flex items-center gap-2" style={{ fontSize: 13, color: TEXT_MUTED }}>
            <CheckCircle size={14} color={GREEN} />
            <span>{t('onboarding.autofill.privacy2', 'Review and edit everything before saving')}</span>
          </div>
        </div>

        {error && (
          <div
            className="rounded-[12px]"
            style={{
              marginTop: 14,
              padding: 12,
              background: '#FEF3C7',
              border: '1px solid #FBBF24',
              fontSize: 13.5,
              color: '#92400E',
              lineHeight: 1.4,
            }}
          >
            {error}
          </div>
        )}
      </ScrollBody>

      <Footer>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = '' }}
        />
        <PrimaryButton onClick={onNext}>
          {t('common.continue', 'Continue')}
        </PrimaryButton>
        {autofillDocuments.length === 0 ? (
          <SecondaryButton onClick={handleSkip}>
            {t('onboarding.autofill.skip', "I don't have one to upload")}
          </SecondaryButton>
        ) : (
          <SecondaryButton onClick={() => fileInputRef.current?.click()}>
            {t('onboarding.autofill.addMore')}
          </SecondaryButton>
        )}
      </Footer>

      <style>{`
        @keyframes sparkle-rotate {
          0%,100% { transform: rotate(0deg) scale(1); opacity: 1; }
          50% { transform: rotate(180deg) scale(1.1); opacity: .7; }
        }
      `}</style>
    </OnboardingShell>
  )
}
